import React, { useState, useEffect } from "react";
import {
  Modal,
  Button,
  Form,
  Table,
  Badge,
  Alert,
  ProgressBar,
  InputGroup,
  Row,
  Col,
  ButtonGroup,
  ToggleButton,
} from "react-bootstrap";
import {
  IMerchantSimple,
  checkoutTechnologyType,
  MerchantStatusType,
} from "../../interfaces";
import { api } from "../../api";
import { useApi } from "../../hooks";

interface MerchantTestModalProps {
  show: boolean;
  onHide: () => void;
  onSuccess: () => void;
  merchants?: IMerchantSimple[] | undefined; // Still accept merchants but make them optional
}

type TestMode = "filter" | "manual";

interface TestFilters {
  checkoutType: string;
  statusType: string;
  updatedAtPreset: string; // "24h", "3d", "7d", "custom", ""
  updatedAtFrom: string;
  updatedAtTo: string;
}

export const MerchantTestModal: React.FC<MerchantTestModalProps> = ({
  show,
  onHide,
  onSuccess,
  merchants = [],
}) => {
  const { post } = useApi();
  const [testMode, setTestMode] = useState<TestMode>("filter");
  const [selectedMerchants, setSelectedMerchants] = useState<Set<string>>(
    new Set()
  );
  const [nameFilter, setNameFilter] = useState<string>("");
  const [domainFilter, setDomainFilter] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoadingPreview, setIsLoadingPreview] = useState<boolean>(false);
  const [results, setResults] = useState<{
    success: string[];
    failed: { merchant: string; error: string }[];
  }>({ success: [], failed: [] });

  const [filters, setFilters] = useState<TestFilters>({
    checkoutType: "",
    statusType: "",
    updatedAtPreset: "",
    updatedAtFrom: "",
    updatedAtTo: "",
  });

  const [previewMerchants, setPreviewMerchants] = useState<IMerchantSimple[]>(
    []
  );
  const [previewCount, setPreviewCount] = useState<number>(0);

  // Filter merchants for manual mode based on the search criteria
  const filteredMerchants =
    merchants?.filter((merchant) => {
      const nameMatch =
        !nameFilter ||
        merchant.name.toLowerCase().includes(nameFilter.toLowerCase());

      const domainMatch =
        !domainFilter ||
        merchant.homePageUrl.toLowerCase().includes(domainFilter.toLowerCase());

      return nameMatch && domainMatch;
    }) || [];

  const getMerchantId = (merchant: IMerchantSimple) => merchant._id;

  // Load preview when filters change in filter mode
  useEffect(() => {
    if (testMode === "filter" && show) {
      loadPreview();
    }
  }, [filters, testMode, show]);

  // Calculate date range based on preset
  const getDateRange = () => {
    const now = new Date();
    let from: Date | undefined;
    let to: Date | undefined = now;

    switch (filters.updatedAtPreset) {
      case "24h":
        from = new Date(now.getTime() - 24 * 60 * 60 * 1000);
        break;
      case "3d":
        from = new Date(now.getTime() - 3 * 24 * 60 * 60 * 1000);
        break;
      case "7d":
        from = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        break;
      case "custom":
        from = filters.updatedAtFrom
          ? new Date(filters.updatedAtFrom)
          : undefined;
        to = filters.updatedAtTo ? new Date(filters.updatedAtTo) : undefined;
        break;
      default:
        from = undefined;
        to = undefined;
    }

    return { from, to };
  };

  const loadPreview = async () => {
    // Only load if at least one filter is set
    if (
      !filters.checkoutType &&
      !filters.statusType &&
      !filters.updatedAtPreset
    ) {
      setPreviewMerchants([]);
      setPreviewCount(0);
      return;
    }

    setIsLoadingPreview(true);
    try {
      const { from, to } = getDateRange();

      const response = await post<{
        count: number;
        merchants: IMerchantSimple[];
      }>("test/browser/preview", {
        filters: {
          checkoutType: filters.checkoutType || undefined,
          statusType: filters.statusType || undefined,
          updatedAtFrom: from?.toISOString(),
          updatedAtTo: to?.toISOString(),
        },
      });

      setPreviewMerchants(response.merchants);
      setPreviewCount(response.count);
    } catch (error) {
      console.error("Failed to load preview:", error);
      setPreviewMerchants([]);
      setPreviewCount(0);
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const toggleMerchantSelection = (merchantId: string) => {
    const newSelection = new Set(selectedMerchants);
    if (newSelection.has(merchantId)) {
      newSelection.delete(merchantId);
    } else {
      newSelection.add(merchantId);
    }
    setSelectedMerchants(newSelection);
  };

  const toggleSelectAll = () => {
    if (selectedMerchants.size === filteredMerchants.length) {
      setSelectedMerchants(new Set());
    } else {
      const allMerchantIds = new Set(filteredMerchants.map(getMerchantId));
      setSelectedMerchants(allMerchantIds);
    }
  };

  const handleClose = () => {
    setNameFilter("");
    setDomainFilter("");
    setSelectedMerchants(new Set());
    setIsLoading(false);
    setResults({ success: [], failed: [] });
    setFilters({
      checkoutType: "",
      statusType: "",
      updatedAtPreset: "",
      updatedAtFrom: "",
      updatedAtTo: "",
    });
    setPreviewMerchants([]);
    setPreviewCount(0);
    onHide();
  };

  const runTests = async () => {
    if (testMode === "manual" && selectedMerchants.size === 0) {
      return;
    }

    if (testMode === "filter" && previewCount === 0) {
      return;
    }

    setIsLoading(true);
    setResults({ success: [], failed: [] });

    try {
      if (testMode === "manual") {
        // Convert the selectedMerchants set to an array of merchant IDs
        const merchantIds = Array.from(selectedMerchants);

        // Call the bulk test endpoint with merchant IDs
        const response = await post<{
          message: string;
          results: {
            scheduled: string[];
            failed: { merchantId: string; error: string }[];
          };
        }>("test/browser/bulk", {
          merchantIds,
        });

        // Update results based on the response
        const { results: bulkResults } = response;

        // Map merchant IDs back to names for display
        const successNames = bulkResults.scheduled
          .map((id: string) => {
            const merchant = merchants?.find((m) => getMerchantId(m) === id);
            return merchant?.name || id;
          })
          .filter(Boolean);

        const failedDetails = bulkResults.failed.map(
          (failure: { merchantId: string; error: string }) => {
            const merchant = merchants?.find(
              (m) => getMerchantId(m) === failure.merchantId
            );
            return {
              merchant: merchant?.name || failure.merchantId,
              error: failure.error,
            };
          }
        );

        setResults({
          success: successNames,
          failed: failedDetails,
        });
      } else {
        // Filter mode - use filters
        const { from, to } = getDateRange();

        const response = await post<{
          message: string;
          results: {
            scheduled: string[];
            failed: { merchantId: string; error: string }[];
          };
        }>("test/browser/bulk", {
          filters: {
            checkoutType: filters.checkoutType || undefined,
            statusType: filters.statusType || undefined,
            updatedAtFrom: from?.toISOString(),
            updatedAtTo: to?.toISOString(),
          },
        });

        const { results: bulkResults } = response;

        // For filter mode, we use the preview merchants for name mapping
        const successNames = bulkResults.scheduled
          .map((id: string) => {
            const merchant = previewMerchants.find((m) => m._id === id);
            return merchant?.name || id;
          })
          .filter(Boolean);

        const failedDetails = bulkResults.failed.map(
          (failure: { merchantId: string; error: string }) => {
            const merchant = previewMerchants.find(
              (m) => m._id === failure.merchantId
            );
            return {
              merchant: merchant?.name || failure.merchantId,
              error: failure.error,
            };
          }
        );

        setResults({
          success: successNames,
          failed: failedDetails,
        });
      }
    } catch (error) {
      console.error("Failed to run bulk tests:", error);
      const errorMessage =
        error instanceof Error ? error.message : "Failed to schedule tests";
      setResults({
        success: [],
        failed: [
          {
            merchant: "Bulk Test",
            error: errorMessage,
          },
        ],
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getSelectedCount = () => selectedMerchants.size;
  const getTotalCount = () => filteredMerchants.length;

  return (
    <Modal show={show} onHide={handleClose} size="xl" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>Test Multiple Merchants</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ maxHeight: "70vh", overflowY: "auto" }}>
        {!isLoading &&
          results.success.length === 0 &&
          results.failed.length === 0 && (
            <>
              {/* Mode Selection */}
              <Row className="mb-4">
                <Col>
                  <Form.Label>Selection Mode</Form.Label>
                  <ButtonGroup className="d-flex">
                    <ToggleButton
                      id="mode-filter"
                      type="radio"
                      variant={
                        testMode === "filter" ? "primary" : "outline-primary"
                      }
                      name="mode"
                      value="filter"
                      checked={testMode === "filter"}
                      onChange={(e) =>
                        setTestMode(e.currentTarget.value as TestMode)
                      }
                    >
                      <i className="bi bi-funnel me-2"></i>
                      Filter Mode
                    </ToggleButton>
                    <ToggleButton
                      id="mode-manual"
                      type="radio"
                      variant={
                        testMode === "manual" ? "primary" : "outline-primary"
                      }
                      name="mode"
                      value="manual"
                      checked={testMode === "manual"}
                      onChange={(e) =>
                        setTestMode(e.currentTarget.value as TestMode)
                      }
                    >
                      <i className="bi bi-list-check me-2"></i>
                      Manual Selection
                    </ToggleButton>
                  </ButtonGroup>
                  <Form.Text className="text-muted d-block mt-2">
                    {testMode === "filter"
                      ? "Schedule tests using filters (checkout type, status, update date)"
                      : "Manually select merchants from the list"}
                  </Form.Text>
                </Col>
              </Row>

              {/* Filter Mode UI */}
              {testMode === "filter" && (
                <>
                  <Row className="mb-3">
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label>Checkout Technology</Form.Label>
                        <Form.Select
                          value={filters.checkoutType}
                          onChange={(e) =>
                            setFilters({
                              ...filters,
                              checkoutType: e.target.value,
                            })
                          }
                        >
                          <option value="">All Technologies</option>
                          {Object.values(checkoutTechnologyType).map((type) => (
                            <option key={type} value={type}>
                              {type}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label>Status</Form.Label>
                        <Form.Select
                          value={filters.statusType}
                          onChange={(e) =>
                            setFilters({
                              ...filters,
                              statusType: e.target.value,
                            })
                          }
                        >
                          <option value="">All Statuses</option>
                          {Object.values(MerchantStatusType).map((status) => (
                            <option key={status} value={status}>
                              {status}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label>Updated Within</Form.Label>
                        <Form.Select
                          value={filters.updatedAtPreset}
                          onChange={(e) =>
                            setFilters({
                              ...filters,
                              updatedAtPreset: e.target.value,
                            })
                          }
                        >
                          <option value="">Any Time</option>
                          <option value="24h">Last 24 Hours</option>
                          <option value="3d">Last 3 Days</option>
                          <option value="7d">Last 7 Days</option>
                          <option value="custom">Custom Range</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  </Row>

                  {/* Custom Date Range */}
                  {filters.updatedAtPreset === "custom" && (
                    <Row className="mb-3">
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>From Date</Form.Label>
                          <Form.Control
                            type="datetime-local"
                            value={filters.updatedAtFrom}
                            onChange={(e) =>
                              setFilters({
                                ...filters,
                                updatedAtFrom: e.target.value,
                              })
                            }
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label>To Date</Form.Label>
                          <Form.Control
                            type="datetime-local"
                            value={filters.updatedAtTo}
                            onChange={(e) =>
                              setFilters({
                                ...filters,
                                updatedAtTo: e.target.value,
                              })
                            }
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                  )}

                  {/* Preview Section */}
                  <Alert variant="info" className="mb-3">
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <strong>
                          {isLoadingPreview ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-2" />
                              Loading preview...
                            </>
                          ) : (
                            <>
                              {previewCount} merchant
                              {previewCount !== 1 ? "s" : ""} will be scheduled
                              for testing
                            </>
                          )}
                        </strong>
                      </div>
                      {previewCount > 0 && !isLoadingPreview && (
                        <Button
                          size="sm"
                          variant="outline-info"
                          onClick={() =>
                            setFilters({
                              checkoutType: "",
                              statusType: "",
                              updatedAtPreset: "",
                              updatedAtFrom: "",
                              updatedAtTo: "",
                            })
                          }
                        >
                          Clear Filters
                        </Button>
                      )}
                    </div>
                  </Alert>

                  {/* Preview List */}
                  {previewCount > 0 && !isLoadingPreview && (
                    <div style={{ maxHeight: "300px", overflowY: "auto" }}>
                      <Table striped bordered hover size="sm">
                        <thead>
                          <tr>
                            <th>Merchant Name</th>
                            <th>Domain</th>
                            <th>Technology</th>
                            <th>Status</th>
                            <th>Last Updated</th>
                          </tr>
                        </thead>
                        <tbody>
                          {previewMerchants.map((merchant) => (
                            <tr key={merchant._id}>
                              <td>{merchant.name}</td>
                              <td>{merchant.homePageUrl}</td>
                              <td>
                                <Badge bg="secondary">
                                  {merchant.checkoutType}
                                </Badge>
                              </td>
                              <td>
                                {merchant.statusType ? (
                                  <Badge
                                    bg={
                                      merchant.statusType ===
                                      MerchantStatusType.HEALTHY
                                        ? "success"
                                        : merchant.statusType ===
                                          MerchantStatusType.NO_COUPONS
                                        ? "warning"
                                        : "danger"
                                    }
                                  >
                                    {merchant.statusType}
                                  </Badge>
                                ) : (
                                  <Badge bg="secondary">Unknown</Badge>
                                )}
                              </td>
                              <td>
                                {merchant.updatedAt
                                  ? new Date(
                                      merchant.updatedAt
                                    ).toLocaleString()
                                  : "N/A"}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  )}
                </>
              )}

              {/* Manual Selection Mode UI */}
              {testMode === "manual" && (
                <>
                  {/* Filter Section */}
                  <Row className="mb-3">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Filter by Name</Form.Label>
                        <InputGroup>
                          <Form.Control
                            type="text"
                            placeholder="Search by merchant name..."
                            value={nameFilter}
                            onChange={(e) => setNameFilter(e.target.value)}
                          />
                          {nameFilter && (
                            <Button
                              variant="outline-secondary"
                              onClick={() => setNameFilter("")}
                              title="Clear name filter"
                            >
                              <i className="bi bi-x"></i>
                            </Button>
                          )}
                        </InputGroup>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Filter by Domain</Form.Label>
                        <InputGroup>
                          <Form.Control
                            type="text"
                            placeholder="Search by domain..."
                            value={domainFilter}
                            onChange={(e) => setDomainFilter(e.target.value)}
                          />
                          {domainFilter && (
                            <Button
                              variant="outline-secondary"
                              onClick={() => setDomainFilter("")}
                              title="Clear domain filter"
                            >
                              <i className="bi bi-x"></i>
                            </Button>
                          )}
                        </InputGroup>
                      </Form.Group>
                    </Col>
                  </Row>

                  {/* Merchant Selection Summary */}
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <div>
                      <strong>
                        {getSelectedCount()} of {getTotalCount()} merchants
                        selected
                      </strong>
                    </div>
                  </div>

                  {/* Merchant List */}
                  <div>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <Form.Check
                        type="checkbox"
                        id="select-all-checkbox"
                        label="Select All"
                        checked={
                          selectedMerchants.size === filteredMerchants.length &&
                          filteredMerchants.length > 0
                        }
                        onChange={toggleSelectAll}
                      />
                      <Badge bg="secondary">
                        {getSelectedCount()} selected
                      </Badge>
                    </div>

                    <div style={{ maxHeight: "300px", overflowY: "auto" }}>
                      <Table striped bordered hover size="sm">
                        <thead>
                          <tr>
                            <th style={{ width: "50px" }}>Select</th>
                            <th>Merchant Name</th>
                            <th>Domain</th>
                            <th>Country</th>
                            <th>Schedule</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredMerchants.map((merchant) => (
                            <tr key={merchant._id}>
                              <td className="text-center">
                                <Form.Check
                                  type="checkbox"
                                  checked={selectedMerchants.has(
                                    getMerchantId(merchant)
                                  )}
                                  onChange={() =>
                                    toggleMerchantSelection(
                                      getMerchantId(merchant)
                                    )
                                  }
                                />
                              </td>
                              <td>{merchant.name}</td>
                              <td>{merchant.homePageUrl}</td>
                              <td>{merchant.countryCode}</td>
                              <td>
                                {merchant.schedule?.enabled ? (
                                  <Badge bg="success">Scheduled</Badge>
                                ) : (
                                  <Badge bg="secondary">Not Scheduled</Badge>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  </div>
                </>
              )}
            </>
          )}
        {/* Loading State */}
        {isLoading && (
          <div className="text-center my-4">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Scheduling tests...</span>
            </div>
            <div className="mt-2">
              Scheduling tests for{" "}
              {testMode === "manual" ? selectedMerchants.size : previewCount}{" "}
              merchants...
            </div>
            <ProgressBar animated now={100} className="mt-2" />
          </div>
        )}{" "}
        {/* Test Results */}
        {!isLoading &&
          (results.success.length > 0 || results.failed.length > 0) && (
            <Alert
              variant={results.failed.length === 0 ? "success" : "warning"}
            >
              <Alert.Heading>Tests Scheduled!</Alert.Heading>
              <p>
                <strong>Successfully scheduled:</strong>{" "}
                {results.success.length} tests
              </p>

              {results.success.length > 0 && (
                <div className="mb-3">
                  <h6>Scheduled Tests:</h6>
                  <ul>
                    {results.success.map((name, index) => (
                      <li key={index}>{name}</li>
                    ))}
                  </ul>
                </div>
              )}

              {results.failed.length > 0 && (
                <>
                  <hr />
                  <h6>Failed to Schedule:</h6>
                  <ul>
                    {results.failed.map((error, index) => (
                      <li key={index}>
                        <strong>{error.merchant}:</strong> {error.error}
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </Alert>
          )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          {!isLoading &&
          (results.success.length > 0 || results.failed.length > 0)
            ? "Close"
            : "Cancel"}
        </Button>
        {!isLoading &&
          results.success.length === 0 &&
          results.failed.length === 0 && (
            <Button
              variant="primary"
              onClick={runTests}
              disabled={
                testMode === "manual"
                  ? selectedMerchants.size === 0
                  : previewCount === 0 || isLoadingPreview
              }
            >
              {testMode === "manual"
                ? `Schedule ${getSelectedCount()} Merchants for Testing`
                : `Schedule ${previewCount} Merchants for Testing`}
            </Button>
          )}
      </Modal.Footer>
    </Modal>
  );
};
